import { useRef } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export function Magnetic({ children, strength = 0.25, className = "" }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 18, mass: 0.4 });
  const reduced = useReducedMotion();
  const onMove = (e) => {
    if (reduced) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy }} className={`inline-block ${className}`}>
      {children}
    </motion.div>
  );
}

const base = "group relative inline-flex shrink-0 items-center gap-3 label whitespace-nowrap transition-colors duration-500 ease-out";
const variants = {
  solid: "bg-ink text-paper px-8 py-5 hover:bg-ink-2",
  light: "bg-paper text-ink px-8 py-5 hover:bg-white",
  outline: "border border-ink text-ink px-8 py-5 hover:bg-ink hover:text-paper",
  outlineLight: "border border-paper/40 text-paper px-8 py-5 hover:border-paper",
  ghost: "text-ink py-2",
  ghostLight: "text-paper py-2",
};

export function Button({ to, href, onClick, children, variant = "solid", className = "", arrow = true, magnetic = true, type = "button", ...rest }) {
  const cls = `${base} ${variants[variant]} ${className}`;
  const inner = (
    <>
      <span className="inline-flex items-center gap-2">{children}</span>
      {arrow && <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform duration-500 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
      <span className="pointer-events-none absolute left-8 right-8 bottom-3 h-px bg-current origin-left scale-x-0 transition-transform duration-500 ease-out group-hover:scale-x-100 opacity-60" />
    </>
  );
  let el;
  if (to) el = <Link to={to} className={cls} onClick={onClick} {...rest}>{inner}</Link>;
  else if (href) el = <a href={href} className={cls} onClick={onClick} {...rest}>{inner}</a>;
  else el = <button type={type} className={cls} onClick={onClick} {...rest}>{inner}</button>;
  return magnetic ? <Magnetic>{el}</Magnetic> : el;
}

export function TextLink({ to, href, children, className = "", light = false, ...rest }) {
  const cls = `label link-line inline-flex items-center gap-2 ${light ? "text-paper" : "text-ink"} ${className}`;
  const inner = (
    <>
      {children}
      <ArrowUpRight size={12} strokeWidth={1.5} />
    </>
  );
  if (href) return <a href={href} className={cls} {...rest}>{inner}</a>;
  return <Link to={to} className={cls} {...rest}>{inner}</Link>;
}
